/** Generic object change history — `/v2/console/changes/`.
 *
 * One read surface for every model tracked by the backend's objchange
 * registry (who / when / what / IP / user agent / reason), plus the
 * whitelist-gated restore action. Rendered by `src/components/History/`.
 */
import type { AIChangeRef } from './aiChanges';
import { apiGet, apiPost, withChangeReason } from './client';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

export type ChangeAction = 'created' | 'updated' | 'deleted' | 'restored';

export interface ObjectChange {
  id: number;
  created_ts: string;
  /** `app_label.model` of the changed object. */
  content_type: string;
  object_pk: string;
  object_repr: string;
  tour_operator_id: number | null;
  aggregate_type: string | null;
  aggregate_pk: string | null;
  action: ChangeAction;
  actor: { id: number | null; email: string } | null;
  source: string;
  /** Free-text reason (X-Change-Reason header / backend actor block). */
  comment: string;
  ip: string | null;
  user_agent: string;
  correlation_id: string;
  root_request_id: string;
  task_id: string | null;
  import_run_id: string | null;
  changed_paths: string[];
  restorable: boolean;
  /** The AI change set that wrote this row (its correlation id), for the History-tab chip. */
  change_set?: AIChangeRef | null;
}

export interface ObjectChangeDetail extends ObjectChange {
  /** Changed fields only (full row for create/delete); secret-masked.
   *  Null on metadata-only rows (integration/import sources). */
  state_before: Record<string, unknown> | null;
  state_after: Record<string, unknown> | null;
  restorable_fields: string[];
  /** Read-time FK repr resolution: {fk_attname: {id: label}} for the ids in
   *  the snapshots, so `image_id 12 → 15` reads as names. */
  display?: Record<string, Record<string, string>>;
}

export interface ChangeFilters {
  content_type?: string;
  object_pk?: string;
  /** Also include child-model rows grouped under this object. */
  aggregate?: boolean;
  /** `<app_label.model>:<pk>` — a second root merged into the object's history, one list in
   *  `created_ts` order (declared pairs only: an itinerary group with its default itinerary,
   *  `demo.itinerary:<id>`). Needs the root kind's view permission on top of
   *  `console_audit_view` (403); an undeclared or foreign root is a 400. List only. */
  also?: string;
  action?: ChangeAction;
  source?: string;
  actor?: number;
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
  /** Everything one background task (e.g. a batch edit) changed. */
  task_id?: string;
  path?: string;
  created_after?: string;
  created_before?: string;
  ordering?: string;
  limit?: number;
  offset?: number;
}

function toQuery(f: ChangeFilters): string {
  const usp = new URLSearchParams();
  if (f.content_type) usp.set('content_type', f.content_type);
  if (f.object_pk) usp.set('object_pk', f.object_pk);
  if (f.aggregate) usp.set('aggregate', '1');
  if (f.also) usp.set('also', f.also);
  if (f.action) usp.set('action', f.action);
  if (f.source) usp.set('source', f.source);
  if (f.actor) usp.set('actor', String(f.actor));
  if (f.tour_operator_id) usp.set('tour_operator_id', String(f.tour_operator_id));
  if (f.task_id) usp.set('task_id', f.task_id);
  if (f.path) usp.set('path', f.path);
  if (f.created_after) usp.set('created_after', f.created_after);
  if (f.created_before) usp.set('created_before', f.created_before);
  if (f.ordering) usp.set('ordering', f.ordering);
  if (f.limit) usp.set('limit', String(f.limit));
  if (f.offset) usp.set('offset', String(f.offset));
  return usp.toString();
}

export async function list(filters: ChangeFilters): Promise<PaginatedResponse<ObjectChange>> {
  const qs = toQuery(filters);
  return apiGet(`/changes/${qs ? `?${qs}` : ''}`);
}

export async function retrieve(id: number): Promise<ObjectChangeDetail> {
  return apiGet(`/changes/${id}/`);
}

export interface RestoreResult {
  restored_fields: string[];
  change_id: number | null;
}

export async function restore(
  id: number,
  body: { fields?: string[]; expected_updated_ts?: string },
  reason?: string,
): Promise<RestoreResult> {
  return apiPost(`/changes/${id}/restore/`, body, withChangeReason(reason));
}

// ---- mass restore (POST /changes/batch/, action=restore) -------------------

export type BatchRestoreOutcome =
  | 'restored'
  | 'would_restore'
  | 'no_change'
  | 'stale'
  | 'missing'
  | 'not_restorable'
  | 'failed';

export const RESTORE_OUTCOME_LABELS: Record<BatchRestoreOutcome, string> = {
  restored: 'Restored',
  would_restore: 'Would restore',
  no_change: 'Already restored',
  stale: 'Changed since — skipped',
  missing: 'Gone',
  not_restorable: 'Not restorable',
  failed: 'Failed',
};

export const RESTORE_OUTCOME_SEVERITY: Record<
  BatchRestoreOutcome,
  'success' | 'info' | 'warning' | 'error'
> = {
  restored: 'success',
  would_restore: 'success',
  no_change: 'info',
  stale: 'warning',
  missing: 'warning',
  not_restorable: 'warning',
  failed: 'error',
};

export interface BatchRestoreRow {
  /** The Object_Change row id. */
  id: number;
  outcome: BatchRestoreOutcome;
  object: {
    content_type: string;
    object_pk: string;
    repr: string;
    tenant_id: number | null;
  } | null;
  fields?: string[];
  error?: string;
}

export interface BatchRestoreTaskResult {
  dry_run: boolean;
  resource: 'changes';
  total: number;
  counts: Partial<Record<BatchRestoreOutcome, number>>;
  results: BatchRestoreRow[];
  results_truncated: boolean;
}
